import { useEffect, useRef } from 'react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Chip,
  Button,
  Tab,
  Tabs,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material';
import ReviewListToolbar from '../../wizards/ReviewListToolbar';
import ReviewStoryCard from '../../wizards/ReviewStoryCard';
import ReviewItemCard from '../../wizards/ReviewItemCard';
import TestSuiteSelector from '../../../components/project-dashboard/test-cases/ai/TestSuiteSelector';
import JiraReviewScreen from '../../../components/project-dashboard/test-cases/ai/JiraReviewScreen';

// ── UploadScreen ──────────────────────────────────────────────────────────────

export const UploadScreen = ({
  uploadedFile,
  onFileChange,
  confluenceUrl,
  onUrlChange,
  inputTab,
  onTabChange,
}) => {
  const fileInputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) onFileChange(file);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography
          variant="overline"
          fontWeight={700}
          color="text.secondary"
          sx={{ letterSpacing: 1 }}>
          INPUT SOURCE
        </Typography>
        <Tabs
          value={inputTab}
          onChange={(_, v) => onTabChange(v)}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            mt: 0.5,
            minHeight: 36,
          }}
          TabIndicatorProps={{ style: { height: 2 } }}>
          <Tab
            value="url"
            label={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontSize: '0.8rem',
                }}>
                <LinkIcon sx={{ fontSize: 16 }} /> Confluence URL
              </Box>
            }
            sx={{
              minHeight: 36,
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 500,
            }}
          />
          <Tab
            value="file"
            label={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontSize: '0.8rem',
                }}>
                <UploadFileIcon sx={{ fontSize: 16 }} /> Upload file
              </Box>
            }
            sx={{
              minHeight: 36,
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 500,
            }}
          />
        </Tabs>
      </Box>

      {inputTab === 'file' && (
        <Box
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: uploadedFile ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            bgcolor: uploadedFile ? 'primary.50' : 'grey.50',
            transition: 'all 0.15s ease',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
          }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx"
            style={{ display: 'none' }}
            onChange={(e) => onFileChange(e.target.files?.[0] || null)}
          />
          {uploadedFile ? (
            <>
              <AttachFileIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="body2" fontWeight={600} color="primary.main">
                {uploadedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click to replace
              </Typography>
            </>
          ) : (
            <>
              <UploadFileIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
              <Typography variant="body2">
                <Box component="span" fontWeight={700} color="primary.main">
                  Click to upload
                </Box>{' '}
                or drag and drop
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PDF, DOCX, Confluence export (max 20 MB)
              </Typography>
            </>
          )}
        </Box>
      )}

      {inputTab === 'url' && (
        <TextField
          fullWidth
          size="small"
          placeholder="https://yourcompany.atlassian.net/wiki/spaces/…"
          value={confluenceUrl}
          onChange={(e) => onUrlChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <LinkIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 1 }} />
            ),
          }}
        />
      )}
    </Box>
  );
};

// ── ReviewStoriesScreen ───────────────────────────────────────────────────────

export const ReviewStoriesScreen = ({
  stories,
  onStoriesChange,
  tokenUsage,
  onEditStory,
  linkedJiraProjects = [],
  hasReporterWarning = false,
}) => {
  const allSelected = stories?.every((s) => s.selected) ?? true;
  const selectedCount = stories?.filter((s) => s.selected).length ?? 0;
  const someSelected = !allSelected && selectedCount > 0;
  const selectedWithNoProject =
    stories?.filter((s) => s.selected && !s.jiraProjectId) ?? [];

  const toggle = (id) =>
    onStoriesChange(
      stories?.map((s) => (s.id === id ? { ...s, selected: !s.selected } : s)),
    );
  const toggleAll = () =>
    onStoriesChange(stories?.map((s) => ({ ...s, selected: !allSelected })));

  const handleProjectChange = (storyId, projectId) =>
    onStoriesChange(
      stories?.map((s) =>
        s.id === storyId ? { ...s, jiraProjectId: projectId } : s,
      ),
    );

  const handleAcceptLabel = (storyId, lbl) =>
    onStoriesChange(
      stories?.map((s) =>
        s.id === storyId
          ? {
              ...s,
              labels: s.labels?.includes(lbl)
                ? s.labels
                : [...(s.labels || []), lbl],
              suggestedLabels: (s.suggestedLabels || []).filter(
                (l) => l !== lbl,
              ),
            }
          : s,
      ),
    );

  const handleDismissLabel = (storyId, lbl) =>
    onStoriesChange(
      stories?.map((s) =>
        s.id === storyId
          ? {
              ...s,
              suggestedLabels: (s.suggestedLabels || []).filter(
                (l) => l !== lbl,
              ),
            }
          : s,
      ),
    );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {hasReporterWarning && (
        <Alert
          severity="warning"
          icon={<InfoOutlinedIcon fontSize="small" />}
          sx={{ borderRadius: 1.5 }}>
          <Typography variant="body2" fontWeight={600}>
            Reporter not set
          </Typography>
          <Typography
            variant="caption"
            color="inherit"
            sx={{ display: 'block', mt: 0.25 }}>
            Your account is not linked to a Jira account. You will need to
            manually select a Reporter in each story's edit form before creating
            issues in Jira.
          </Typography>
        </Alert>
      )}

      <ReviewListToolbar
        allSelected={allSelected}
        someSelected={someSelected}
        selectedCount={selectedCount}
        totalCount={stories.length}
        onToggleAll={toggleAll}
        tokenUsage={tokenUsage}
        warning={
          selectedWithNoProject.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
              <Typography
                variant="caption"
                color="warning.dark"
                fontWeight={500}>
                {selectedWithNoProject.length}{' '}
                {selectedWithNoProject.length === 1 ? 'requires' : 'require'}{' '}
                Jira project
              </Typography>
            </Box>
          )
        }
      />

      {stories.map((story) => (
        <ReviewStoryCard
          key={story.id}
          story={story}
          onToggle={toggle}
          onEdit={onEditStory}
          linkedProjects={linkedJiraProjects}
          onProjectChange={(pid) => handleProjectChange(story.id, pid)}
          suggestedLabels={story.suggestedLabels ?? []}
          onAcceptLabel={(lbl) => handleAcceptLabel(story.id, lbl)}
          onDismissLabel={(lbl) => handleDismissLabel(story.id, lbl)}
        />
      ))}
    </Box>
  );
};

// ── GenerateTCsScreen ─────────────────────────────────────────────────────────

// const QUANTITY_OPTIONS = [
//   {
//     value: 'multiple',
//     icon: <FormatListNumberedIcon sx={{ fontSize: 16 }} />,
//     label: 'Multiple per story',
//   },
//   {
//     value: 'single',
//     icon: <LooksOneIcon sx={{ fontSize: 16 }} />,
//     label: 'Single per story',
//   },
// ];

// const TOGGLE_BTN_SX = {
//   px: 1.5,
//   fontSize: '12px',
//   fontWeight: 600,
//   textTransform: 'none',
//   borderColor: 'divider',
//   '&.Mui-selected': {
//     bgcolor: '#EFF6FF',
//     color: 'primary.main',
//     borderColor: 'primary.main',
//   },
// };

const FieldLabel = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={600}
    color="text.secondary"
    display="block"
    sx={{
      mb: 0.5,
      textTransform: 'uppercase',
      letterSpacing: '0.4px',
      fontSize: '11px',
    }}>
    {children}
  </Typography>
);

export const GenerateTCsScreen = ({
  createdJiraStories = [],
  uploadedFile,
  confluenceUrl,
  inputTab,
  quantity,
  onQuantityChange,
  selectedTestSuites = [],
  onTestSuitesChange,
  testSuites = [],
  projectId,
  onReadyChange,
}) => {
  const suitesSelected = selectedTestSuites.length > 0;
  const canGenerate =
    createdJiraStories.length > 0 && (quantity === 'single' || suitesSelected);

  // const handleQuantityChange = (val) => {
  //   onQuantityChange(val);
  //   const ready =
  //     createdJiraStories.length > 0 && (val === 'single' || suitesSelected);
  //   onReadyChange?.(ready);
  // };

  const handleSuitesChange = (ids) => {
    onTestSuitesChange(ids);
    const ready =
      createdJiraStories.length > 0 &&
      (quantity === 'single' || ids.length > 0);
    onReadyChange?.(ready);
  };
  useEffect(() => {
    onReadyChange?.(canGenerate);
  }, []);

  const contextSource =
    inputTab === 'file' && uploadedFile
      ? uploadedFile.name
      : confluenceUrl || null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
      <Box>
        <FieldLabel>Generating test cases for</FieldLabel>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
          {createdJiraStories?.map((story) => (
            <Chip
              key={story.id ?? story.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {story.key && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        color: 'primary.main',
                        bgcolor: 'primary.50',
                        borderRadius: '3px',
                        px: 0.5,
                        py: '1px',
                      }}>
                      {story.key}
                    </Box>
                  )}
                  <Box
                    component="span"
                    sx={{
                      maxWidth: 220,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                      verticalAlign: 'middle',
                    }}>
                    {story.summary || story.title || 'Untitled story'}
                  </Box>
                </Box>
              }
              size="small"
              sx={{
                height: 28,
                fontSize: '0.72rem',
                fontWeight: 500,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          ))}
        </Box>
      </Box>

      {contextSource && (
        <Alert
          severity="info"
          icon={
            inputTab === 'file' ? (
              <AttachFileIcon fontSize="small" />
            ) : (
              <LinkIcon fontSize="small" />
            )
          }
          sx={{ borderRadius: 1.5, py: 0.75 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
            {inputTab === 'file' ? 'Uploaded document' : 'Confluence URL'} used
            as additional context
          </Typography>
          <Typography
            variant="caption"
            color="inherit"
            sx={{
              display: 'block',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {contextSource} — the AI will use this alongside each story to
            generate more accurate and contextually relevant test cases.
          </Typography>
        </Alert>
      )}

      {/* <Box>
        <FieldLabel>How many test cases per story?</FieldLabel>
        <ToggleButtonGroup
          value={quantity}
          exclusive
          onChange={(_, val) => {
            if (val) handleQuantityChange(val);
          }}
          size="small"
          sx={{ '& .MuiToggleButton-root': TOGGLE_BTN_SX }}>
          {QUANTITY_OPTIONS?.map(({ value, icon, label }) => (
            <ToggleButton key={value} value={value} sx={{ gap: 0.75 }}>
              {icon}
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.5 }}>
          {quantity === 'multiple'
            ? 'Generates a full suite per story — happy path, edge cases, and negative scenarios.'
            : 'Generates one focused, detailed test case per story.'}
        </Typography>
      </Box> */}
      <TestSuiteSelector
        testSuites={testSuites}
        selectedTestSuites={selectedTestSuites}
        onTestSuitesChange={handleSuitesChange}
        projectId={projectId}
      />
    </Box>
  );
};

// ── Reusable Test Case Review Components ─────────────────────────────────────

const COVERAGE_COLORS = {
  'Happy Path': '#22c55e',
  'Happy path': '#22c55e',
  'Edge Case': '#f59e0b',
  'Edge case': '#f59e0b',
  Negative: '#ef4444',
};
const getCoverageColor = (tag) => COVERAGE_COLORS[tag] ?? '#6b7280';

const resolveLabel = (labelId, labels) => {
  const lbl = labels?.find((l) => l.id === labelId);
  return lbl ? lbl.name : labelId;
};

const resolveModule = (moduleId, modules) => {
  const mod = modules?.find((m) => m.id === moduleId);
  return mod ? mod.name : moduleId;
};

// Tech layer dropdown (stub for now, replace with real dropdown as needed)
const TechLayerDropdown = ({ value, onChange, options = [] }) => (
  <TextField
    select
    size="small"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    label="Technology Layer"
    sx={{ minWidth: 120, mr: 1 }}>
    {options.map((opt) => (
      <MenuItem key={opt.id} value={opt.id}>
        {opt.name}
      </MenuItem>
    ))}
  </TextField>
);

// Single test case card (reusable)
export const ReviewTestCaseCard = ({
  tc,
  selected,
  onToggle,
  onEdit,
  showStory,
  story,
  techLayerOptions = [],
  onTechLayerChange,
  labels = [],
  modules = [],
}) => {
  const coverageColor = getCoverageColor(tc.coverageTag);
  const hasWarning = !tc.technologyLayer;
  const stepsCount = tc.testSteps?.length ?? 0;
  return (
    <ReviewItemCard
      key={tc._id}
      id={tc._id}
      selected={selected}
      onToggle={onToggle}
      hasWarning={hasWarning}
      ariaLabel={`Select: ${tc.summary}`}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.75,
          flexWrap: 'wrap',
          gap: 0.5,
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
          }}>
          <Chip
            label={tc.coverageTag || 'Uncategorised'}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 600,
              bgcolor: `${coverageColor}22`,
              color: coverageColor,
              border: `1px solid ${coverageColor}55`,
            }}
          />
          {showStory && (tc.storyKey || story) && (
            <Chip
              label={tc.storyKey || (story?.key ?? '')}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: 'primary.50',
                color: 'primary.main',
                border: '1px solid',
                borderColor: 'primary.200',
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
        </Box>
        {onEdit && (
          <Button
            size="small"
            startIcon={<EditIcon sx={{ fontSize: '12px !important' }} />}
            onClick={() => onEdit(tc)}
            sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
            Edit
          </Button>
        )}
      </Box>

      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
        {tc.summary}
      </Typography>

      {/* Labels */}
      {tc.labels?.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            mb: stepsCount || hasWarning ? 0.75 : 0,
          }}>
          {tc.labels?.map((l, i) => (
            <Chip
              key={i}
              label={resolveLabel(l, labels)}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'grey.100' }}
            />
          ))}
        </Box>
      )}

      {/* Modules (if present) */}
      {tc.module && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
          <Chip
            label={resolveModule(tc.module, modules)}
            size="small"
            sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'grey.200' }}
          />
        </Box>
      )}

      {/* Tech layer dropdown (if editable) */}
      {onTechLayerChange && (
        <TechLayerDropdown
          value={tc.technologyLayer}
          onChange={(val) => onTechLayerChange(tc._id, val)}
          options={techLayerOptions}
        />
      )}

      {hasWarning && !onTechLayerChange && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 13, color: 'warning.main' }} />
          <Typography variant="caption" color="warning.dark" fontWeight={500}>
            Technology layer required before saving
          </Typography>
        </Box>
      )}

      {stepsCount > 0 && (
        <Typography
          variant="caption"
          color="primary.main"
          fontWeight={500}
          sx={{ cursor: 'pointer', display: 'block', mt: 0.5 }}>
          ▶ View steps ({stepsCount})
        </Typography>
      )}
    </ReviewItemCard>
  );
};

export const ReviewTestCaseList = ({
  tcs,
  onTcsChange,
  tokenUsage,
  onEdit,
  showStory = false,
  storyMap = {},
  techLayerOptions = [],
  onTechLayerChange,
  labels = [],
  modules = [],
}) => {
  const allSelected = tcs?.every((t) => t.selected) ?? true;
  const selectedCount = tcs?.filter((t) => t.selected).length ?? 0;
  const someSelected = !allSelected && selectedCount > 0;
  const toggle = (id) =>
    onTcsChange(
      tcs?.map((t) => (t._id === id ? { ...t, selected: !t.selected } : t)),
    );
  const toggleAll = () =>
    onTcsChange(tcs?.map((t) => ({ ...t, selected: !allSelected })));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ReviewListToolbar
        allSelected={allSelected}
        someSelected={someSelected}
        selectedCount={selectedCount}
        totalCount={tcs.length}
        onToggleAll={toggleAll}
        tokenUsage={tokenUsage}
        showLegend
      />
      {tcs?.map((tc) => (
        <ReviewTestCaseCard
          key={tc._id}
          tc={tc}
          selected={tc.selected}
          onToggle={toggle}
          onEdit={onEdit}
          showStory={showStory}
          story={storyMap?.[tc.storyKey]}
          techLayerOptions={techLayerOptions}
          onTechLayerChange={onTechLayerChange}
          labels={labels}
          modules={modules}
        />
      ))}
    </Box>
  );
};
const normaliseTc = (tc, idx) => ({
  _id: tc._id ?? `${tc.story_key}-${idx}`,
  selected: tc.selected ?? true,
  stepsExpanded: tc.stepsExpanded ?? false,

  ...tc,

  coverageTag: tc.coverageTag ?? tc.coverage_tag,
  technologyLayer: tc.technologyLayer ?? tc.technology_layer,
  testSteps: tc.testSteps ?? tc.test_steps,
  storyKey: tc.storyKey ?? tc.story_key,
  storyTitle: tc.storyTitle ?? tc.story_title,

  _crossMatchKey: tc._crossMatchKey ?? tc.cross_match_key,
  _crossMatchScore: tc._crossMatchScore ?? tc.cross_match_score,
  _crossMatchStrength:
    tc._crossMatchStrength ?? tc.cross_match_strength,

  preCondition: tc.preCondition ?? tc.pre_condition,
});

export const ReviewTCsScreen = ({
  tcs,
  onTcsChange,
  tokenUsage,
  onEdit,
  techLayerOptions = [],
  labels = [],
  modules = [],
  existingMatches = [],
  selectedExisting = [],
  onExistingToggle,

  // optional passthroughs for future compatibility
  savePhase,
  saveProgress,
  saveResult,
  selectedStory,
}) => {
  return (
    <JiraReviewScreen
      tcs={tcs.map(normaliseTc)}
      onTcsChange={onTcsChange}
      technologyLayers={techLayerOptions}
      labels={labels}
      modules={modules}
      tokenUsage={tokenUsage}
      onEdit={onEdit}
      existingMatches={existingMatches}
      selectedExisting={selectedExisting}
      onExistingToggle={onExistingToggle}
      savePhase={savePhase}
      saveProgress={saveProgress}
      saveResult={saveResult}
      selectedStory={selectedStory}
    />
  );
};